import { useEffect, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import CustomMDXRemote from "@components/mdx/CustomMDXRemote";
import { CookieBannerDTO } from "@lib/strapi/dtos/content-types/cookieBanner.dto";
import { ConsentChoices, SignalGroup, applyConsent, readConsent } from "@lib/consent";

/**
 * The cookie banner (item 12 / D22), CMS-driven: copy and categories come from
 * the seeded cookie-banner single type, so an editor can reword it without a
 * deploy — but the SIGNAL each category controls is a closed enum in the CMS,
 * so a rewording cannot silently detach a toggle from its consent key.
 *
 * Consent rules the UI enforces, not just states:
 *   - «Απόρριψη» is as prominent as «Αποδοχή όλων» — a banner where reject is
 *     buried is not valid consent.
 *   - Only Necessary is pre-selected; a pre-ticked analytics box is not
 *     an affirmative act.
 *   - No consent, no banner dismissal by scrolling/clicking elsewhere: it
 *     stays until answered, but never blocks reading the page (bottom sheet,
 *     content stays scrollable).
 *
 * Reopening: the footer's «Ρυθμίσεις cookies» dispatches `pw-open-consent`.
 */
export default function CookieBanner({ banner }: { banner?: CookieBannerDTO | null }) {
  const { t } = useTranslation("main");
  const [visible, setVisible] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>({});

  useEffect(() => {
    const stored = readConsent();
    if (!stored) setVisible(true);
    else setChoices(stored);
    const reopen = () => { setDetailed(true); setVisible(true); };
    window.addEventListener("pw-open-consent", reopen);
    return () => window.removeEventListener("pw-open-consent", reopen);
  }, []);

  if (!visible || !banner) return null;
  const categories = banner.categories ?? [];

  const answer = (c: ConsentChoices) => { applyConsent(c); setVisible(false); };
  const all = Object.fromEntries(
    categories.map((c) => [c.signalGroup as SignalGroup, true])
  ) as ConsentChoices;

  return (
    <div role="dialog" aria-modal="false" aria-label={banner.heading}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-violet-200 bg-white shadow-[0_-12px_40px_-18px_rgba(23,17,31,0.35)]">
      <div className="shell py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="font-extrabold text-ink">{banner.heading}</p>
            <div className="mt-1 text-sm text-muted [&_p]:mb-0">
              {banner.prompt ? <CustomMDXRemote content={banner.prompt} typeOfContainer="Plain" /> : null}
            </div>
            {/* prefetch={false}: the banner shows on every first visit, so
                this link was prefetching the cookie-policy data JSON during
                initial load on every page (see header.tsx) */}
            {banner.policyLinkUrl ? (
              <Link href={banner.policyLinkUrl} prefetch={false} className="mt-1 inline-block text-sm font-bold text-violet-700 underline underline-offset-2">
                {banner.policyLinkLabel}
              </Link>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button type="button" onClick={() => answer({})}
              className="rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-050">
              {banner.rejectAllLabel || t('cookies.rejectAll')}
            </button>
            <button type="button" onClick={() => setDetailed(!detailed)}
              className="rounded-full bg-violet-050 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-200">
              {t('cookies.settings')}
            </button>
            <button type="button" onClick={() => answer(all)}
              className="rounded-full bg-violet-700 px-5 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-violet-800">
              {banner.acceptAllLabel || t('cookies.acceptAll')}
            </button>
          </div>
        </div>

        {detailed ? (
          <div className="mt-4 grid gap-3 border-t border-rule pt-4 sm:grid-cols-3">
            {categories.map((cat, i) => {
              const group = cat.signalGroup as SignalGroup;
              const locked = !!cat.disabled;
              const checked = locked || !!choices[group];
              return (
                <label key={i}
                  className={`flex items-start gap-3 rounded-xl border p-3.5 ${locked ? "border-rule bg-violet-050/50" : "cursor-pointer border-rule hover:border-violet-400"}`}>
                  <input type="checkbox" checked={checked} disabled={locked}
                    onChange={(e) => setChoices({ ...choices, [group]: e.target.checked })}
                    className="mt-1 h-4 w-4 accent-violet-700" />
                  <span>
                    <span className="block text-sm font-extrabold text-ink">{cat.title}</span>
                    <span className="mt-0.5 block text-xs text-muted [&_p]:mb-0">
                      {cat.description ? <CustomMDXRemote content={cat.description} typeOfContainer="Plain" /> : null}
                    </span>
                  </span>
                </label>
              );
            })}
            <div className="sm:col-span-3">
              <button type="button" onClick={() => answer(choices)}
                className="rounded-full bg-violet-700 px-6 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-violet-800">
                {banner.savePreferencesLabel || t('cookies.savePreferences')}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
